import React from 'react';
import { Card, Table } from 'react-bootstrap';
import Moment from 'react-moment';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { validate, ValidationError } from 'class-validator';
import { Bloodwork, Client, Ethnicity, Gender, Menopause } from '../../models';
import { MathUtils } from '../../utils';
import { BMIBarChart } from '../charts/BMIBarChart';
import { HeightOption, FT_OPTIONS, IN_OPTIONS, HEIGHT_SELECT_STYLES } from '../client/height-select-options';

// The BMI bar is a Highcharts chart whose only inputs are three primitives, but
// it sits in the same card as the name fields. Memoizing it keeps Highcharts
// out of the keystroke path: typing a last name no longer redraws the chart.
const MemoBMIBarChart = React.memo(BMIBarChart);

type Props = {
  /** The test's embedded client snapshot — what the card shows when not editing. */
  client: Client;
  /** The test itself, read for the height/weight fallback behind BMI. */
  bloodwork: Bloodwork;
  collectionDate: string;
  /** Set only when a previous test is being compared; adds its own column. */
  previousCollectionDate?: string;
  /** Live client records, used to seed height/weight on older snapshots. */
  currentClients?: Client[];
  includeBmiInPdf: boolean;
  onToggleBmiInPdf: () => void;
  preparedBy?: string;
  /**
   * Persists the edited client and returns any server-side errors — an empty
   * array means saved. Absent when the practitioner may not edit, which is what
   * hides the Edit button.
   */
  onSave?: (pending: Client) => Promise<string[]>;
};

type State = {
  editing: boolean;
  pendingClient: Client | null;
  validationErrors: string[];
  isSaving: boolean;
};

/**
 * The Client Information card at the top of the test results analysis.
 *
 * It owns the whole edit cycle — the pending client, its validation messages
 * and the saving flag — on purpose. This state used to live on
 * BloodworkTestAnalysis, so every keystroke in the name fields re-rendered the
 * entire report: 19 marker sections, the Results Overview, every Insights panel
 * and the supplement panels. That was fast enough to look like lag on a quick
 * machine and like a freeze on a slow one. Keeping the edit state here means a
 * keystroke re-renders this card and nothing else; the report is only rebuilt
 * on Save, when the ranges really do change.
 */
export class ClientInfoCard extends React.Component<Props, State> {
  state: State = {
    editing: false,
    pendingClient: null,
    validationErrors: [],
    isSaving: false,
  };

  startEdit = (): void => {
    const { client, bloodwork, currentClients } = this.props;
    if (!client) return;
    const pending = new Client(client);
    // Default height/weight/units from the live client record when missing
    // on the snapshot — covers tests saved before height/weight were added
    // to the client profile. Fall back to bloodwork.clientId since older
    // snapshots may not carry id on the embedded client.
    const liveClientId = client.id ?? bloodwork?.clientId;
    const liveClient = liveClientId && currentClients
      ? Client.findClient(liveClientId, currentClients)
      : undefined;
    if (liveClient) {
      if (pending.height == null && liveClient.height != null) {
        pending.height = liveClient.height;
      }
      if (pending.weight == null && liveClient.weight != null) {
        pending.weight = liveClient.weight;
      }
      if (pending.units == null && liveClient.units != null) {
        pending.units = liveClient.units;
      }
    }
    this.setState({ editing: true, pendingClient: pending, validationErrors: [] });
  };

  cancelEdit = (): void => {
    this.setState({ editing: false, pendingClient: null, validationErrors: [] });
  };

  patchPendingClient = (apply: (client: Client) => void): void => {
    const { pendingClient } = this.state;
    if (!pendingClient) return;
    apply(pendingClient);
    this.forceUpdate();
  };

  private collectValidationMessages(
    pending: Client,
    classValidatorErrors: ValidationError[],
  ): string[] {
    const messages: string[] = [];
    const labels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      gender: 'Birth Gender',
      dateOfBirth: 'Date of Birth',
      ethnicity: 'Ethnicity',
      menopause: 'Menopause/Pregnant',
      trimester: 'Trimester',
      elevation: 'Elevation',
      cigaretteSmoker: 'Cigarette Smoker',
      alcoholDrinker: 'Alcohol Drinker',
      height: 'Height',
      weight: 'Weight',
    };
    classValidatorErrors.forEach((err) => {
      const label = labels[err.property] ?? err.property;
      Object.keys(err.constraints || {}).forEach((c) => {
        messages.push(`${label} ${err.constraints[c]}`);
      });
    });
    if (pending.isTooYoungToBePregnant?.()) {
      messages.push('Date of Birth — client must be 15 years of age or older when Pregnant is selected.');
    }
    if (pending.isTooOld?.()) {
      messages.push('Date of Birth — client cannot be over 200 years of age.');
    }
    return messages;
  }

  save = async (): Promise<void> => {
    const { pendingClient } = this.state;
    const { onSave } = this.props;
    if (!pendingClient || !onSave) return;

    this.setState({ isSaving: true, validationErrors: [] });

    // finally, so the flag clears on EVERY exit. Both awaits below can reject
    // — class-validator on a bad decorator, onSave before its own try — and
    // without this the card is left with Save and Cancel disabled at
    // "Saving…", unrecoverable short of a reload. The inline version this was
    // extracted from guarded it the same way.
    try {
      // Client-side validation first
      const classErrors = await validate(pendingClient);
      const messages = this.collectValidationMessages(pendingClient, classErrors);
      if (messages.length > 0) {
        this.setState({ validationErrors: messages });
        return;
      }

      const serverErrors = await onSave(pendingClient);
      if (serverErrors.length > 0) {
        this.setState({ validationErrors: serverErrors });
        return;
      }
      this.setState({ editing: false, pendingClient: null, validationErrors: [] });
    } catch (err) {
      console.error('Failed to save client info', err);
      this.setState({
        validationErrors: [
          'Something went wrong saving these details. Please try again.',
        ],
      });
    } finally {
      this.setState({ isSaving: false });
    }
  };

  render() {
    const { client, collectionDate, previousCollectionDate } = this.props;
    if (!client) return null;

    return (
      <Card style={{ marginTop: 0 }}>
        <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Client Information</span>
          <label
            htmlFor="include-in-pdf-BMI"
            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
            title="Include the BMI chart in the Analysis PDF"
          >
            <span style={{ marginRight: '6px' }}>Include BMI in PDF</span>
            <input
              id="include-in-pdf-BMI"
              type="checkbox"
              className="lsm-styled-checkbox"
              checked={this.props.includeBmiInPdf}
              onChange={() => this.props.onToggleBmiInPdf()}
            />
          </label>
        </Card.Header>
        <Card.Body>
          <div>
            {this.state.editing && this.state.validationErrors.length > 0 && (
              <div className="alert alert-danger" style={{ padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                <strong>Please fix the following before saving:</strong>
                <ul style={{ marginBottom: 0, paddingLeft: 20, marginTop: 4 }}>
                  {this.state.validationErrors.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            {(() => {
              const editing = this.state.editing;
              const eff: Client = (editing && this.state.pendingClient) ? this.state.pendingClient : client;
              const isFemale = eff.gender === Gender.FEMALE;
              const isPregnant = isFemale && eff.menopause === Menopause.PREGNANT;
              const units: 'US' | 'SI' = eff.units === 'SI' ? 'SI' : 'US';

              const bw = this.props.bloodwork as any;
              const heightCm = eff.height != null ? Number(eff.height) : (bw.height != null ? Number(bw.height) : null);
              const weightLbs = eff.weight != null ? Number(eff.weight) : (bw.weight != null ? Number(bw.weight) : null);
              const bmiNumber = heightCm != null && weightLbs != null
                ? MathUtils.bmi(weightLbs, heightCm)
                : null;
              const bmiSex: 'M' | 'F' | undefined = eff.gender === Gender.MALE
                ? 'M'
                : eff.gender === Gender.FEMALE
                  ? 'F'
                  : undefined;

              const patch = this.patchPendingClient;
              const editableCellStyle: React.CSSProperties = editing
                ? { backgroundColor: '#fff', verticalAlign: 'middle' }
                : { verticalAlign: 'middle' };
              const inputStyle: React.CSSProperties = {
                width: '100%',
                minWidth: 70,
                fontSize: 'inherit',
                padding: '3px 5px',
                border: '1px solid #ced4da',
                borderRadius: 3,
                outline: 'none',
                backgroundColor: '#fff',
              };
              const smallInputStyle: React.CSSProperties = { ...inputStyle, minWidth: 0, width: 48 };

              const textTd = (val: string, onChange: (v: string) => void) => (
                <td style={editableCellStyle}>
                  {editing
                    ? <input type="text" maxLength={50} value={val ?? ''}
                        onChange={(e) => onChange(e.target.value)} style={inputStyle} />
                    : (val || <span style={{ color: '#adb5bd' }}>—</span>)
                  }
                </td>
              );

              const selectTd = (
                val: string,
                opts: { value: string; label: string }[],
                display: React.ReactNode,
                onChange: (v: string) => void,
              ) => (
                <td style={editableCellStyle}>
                  {editing
                    ? <select value={val ?? ''} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
                        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                  }
                </td>
              );

              const dateTd = (
                isoVal: string | undefined,
                display: React.ReactNode,
                onChange: (iso: string | undefined) => void,
              ) => (
                <td style={editableCellStyle}>
                  {editing
                    ? <DatePicker
                        selected={isoVal ? moment(isoVal).toDate() : null}
                        onChange={(d: Date | null) =>
                          onChange(d ? moment(d).startOf('day').toISOString() : undefined)}
                        maxDate={new Date()}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        dateFormat="MM/dd/yyyy"
                        customInput={<input style={inputStyle} />}
                      />
                    : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                  }
                </td>
              );

              const numberTd = (
                val: number | undefined,
                display: React.ReactNode,
                onChange: (v: number | undefined) => void,
                min?: number,
                max?: number,
                suffix?: string,
              ) => (
                <td style={editableCellStyle}>
                  {editing ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="number" min={min} max={max}
                        value={val ?? ''}
                        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                        style={smallInputStyle}
                      />
                      {suffix && <span style={{ fontSize: 11, color: '#666' }}>{suffix}</span>}
                    </span>
                  ) : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)}
                </td>
              );

              const booleanTd = (
                val: boolean,
                display: React.ReactNode,
                onChange: (v: boolean) => void,
              ) => (
                <td style={editableCellStyle}>
                  {editing
                    ? <select value={val ? 'yes' : 'no'} onChange={(e) => onChange(e.target.value === 'yes')} style={inputStyle}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    : display
                  }
                </td>
              );

              const heightTd = () => {
                if (!editing) {
                  return <td style={editableCellStyle}>{eff.height != null ? client.displayHeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                }
                if (units === 'US') {
                  const { ft, inches } = eff.height != null
                    ? MathUtils.cmToFtIn(eff.height)
                    : { ft: '' as any, inches: '' as any };
                  const ftStr = ft === '' ? '' : String(ft);
                  const inStr = inches === '' ? '' : String(inches);
                  return (
                    <td style={editableCellStyle}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <div style={{ width: 70 }}>
                          <Select<HeightOption>
                            isSearchable={false}
                            maxMenuHeight={400}
                            options={FT_OPTIONS}
                            styles={HEIGHT_SELECT_STYLES}
                            value={FT_OPTIONS.find(o => o.value === ftStr) ?? FT_OPTIONS[0]}
                            onChange={(opt) => {
                              const v = opt?.value ?? '';
                              const newFt = v === '' ? 0 : Number(v);
                              const curIn = eff.height != null ? MathUtils.cmToFtIn(eff.height).inches : 0;
                              patch((c) => { c.height = v === '' && (inches === 0 || inches === '')
                                ? undefined
                                : MathUtils.ftInToCm(newFt, Number(curIn) || 0); });
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 11, color: '#666' }}>ft</span>
                        <div style={{ width: 70 }}>
                          <Select<HeightOption>
                            isSearchable={false}
                            maxMenuHeight={400}
                            options={IN_OPTIONS}
                            styles={HEIGHT_SELECT_STYLES}
                            value={IN_OPTIONS.find(o => o.value === inStr) ?? IN_OPTIONS[0]}
                            onChange={(opt) => {
                              const v = opt?.value ?? '';
                              const newIn = v === '' ? 0 : Number(v);
                              const curFt = eff.height != null ? MathUtils.cmToFtIn(eff.height).ft : 0;
                              patch((c) => { c.height = v === '' && (ft === 0 || ft === '')
                                ? undefined
                                : MathUtils.ftInToCm(Number(curFt) || 0, newIn); });
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 11, color: '#666' }}>in</span>
                      </span>
                    </td>
                  );
                }
                return (
                  <td style={editableCellStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <input type="number" min={0}
                        value={eff.height ?? ''}
                        onChange={(e) => patch((c) => { c.height = e.target.value === '' ? undefined : Number(e.target.value); })}
                        style={{ ...smallInputStyle, width: 58 }} />
                      <span style={{ fontSize: 11, color: '#666' }}>cm</span>
                    </span>
                  </td>
                );
              };

              const weightTd = () => {
                if (!editing) {
                  return <td style={editableCellStyle}>{eff.weight != null ? client.displayWeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                }
                const displayed = eff.weight == null
                  ? ''
                  : units === 'SI' ? MathUtils.lbsToKg(eff.weight) : eff.weight;
                return (
                  <td style={editableCellStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <input type="number" min={0} max={units === 'SI' ? 453 : 999}
                        value={displayed === '' ? '' : displayed}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') { patch((c) => { c.weight = undefined; }); return; }
                          const n = Number(raw);
                          if (isNaN(n)) return;
                          patch((c) => { c.weight = units === 'SI' ? MathUtils.kgToLbs(n) : n; });
                        }}
                        style={{ ...smallInputStyle, width: 58 }} />
                      <span style={{ fontSize: 11, color: '#666' }}>{units === 'SI' ? 'kg' : 'lbs'}</span>
                    </span>
                  </td>
                );
              };

              const ethnicityOptions = [
                { value: '',                                label: '—' },
                { value: Ethnicity.WHITE,                   label: Ethnicity.WHITE },
                { value: Ethnicity.AFRICAN_AMERICAN,        label: Ethnicity.AFRICAN_AMERICAN },
                { value: Ethnicity.HISPANIC,                label: Ethnicity.HISPANIC },
                { value: Ethnicity.ASIAN,                   label: Ethnicity.ASIAN },
                { value: Ethnicity.OTHER,                   label: Ethnicity.OTHER },
              ];
              const menopauseOptions = [
                { value: Menopause.PREMENOPAUSE,  label: 'Premenopause' },
                { value: Menopause.POSTMENOPAUSE, label: 'Postmenopause' },
                { value: Menopause.PREGNANT,      label: 'Pregnant' },
              ];
              const genderOptions = [
                { value: Gender.MALE,   label: 'Male' },
                { value: Gender.FEMALE, label: 'Female' },
              ];
              const trimesterOptions = [
                { value: '',  label: '—' },
                { value: '1', label: 'First' },
                { value: '2', label: 'Second' },
                { value: '3', label: 'Third' },
              ];

              return (
                <>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <Table striped bordered hover className="labsmartz-table" style={{ marginBottom: 0, width: 'auto' }}>
                    <thead>
                      <tr>
                        <th>Last Name</th>
                        <th>First Name</th>
                        <th>Sex</th>
                        <th>Collection Date</th>
                        <th>Age at Collection</th>
                        <th>Date of Birth</th>
                        <th>Ethnicity</th>
                        {isFemale && <th>Menopause/Pregnant</th>}
                        {isPregnant && <th>Trimester</th>}
                        <th>Elevation</th>
                        <th>Inhaled Smoke</th>
                        <th>Alcohol Drinker</th>
                        {previousCollectionDate && <th>Previous Collection Date</th>}
                        <th>Height</th>
                        <th>Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="client-row">
                        {textTd(eff.lastName, (v) => patch((c) => { c.lastName = v; }))}
                        {textTd(eff.firstName, (v) => patch((c) => { c.firstName = v; }))}
                        {selectTd(eff.gender ?? '', genderOptions, eff.displayGender,
                          (v) => patch((c) => { c.gender = v as Gender; }))}
                        <td title="Collection Date is set when the test is created; edit it from the test entry form."
                          style={{ verticalAlign: 'middle' }}>
                          <Moment format="MM/DD/YYYY">{collectionDate}</Moment>
                        </td>
                        <td title="Derived from Date of Birth and Collection Date."
                          style={{ verticalAlign: 'middle' }}>{eff.ageAsOf(collectionDate)}</td>
                        {dateTd(
                          eff.dateOfBirth,
                          <Moment format="MM/DD/YYYY">{eff.dateOfBirth}</Moment>,
                          (iso) => patch((c) => { c.dateOfBirth = iso; }),
                        )}
                        {selectTd(eff.ethnicity ?? '', ethnicityOptions, eff.displayEthnicity,
                          (v) => patch((c) => { c.ethnicity = v as Ethnicity; }))}
                        {isFemale && selectTd(
                          eff.menopause ?? '', menopauseOptions, eff.displayMenopause,
                          (v) => patch((c) => {
                            c.menopause = v as Menopause;
                            if (v !== Menopause.PREGNANT) { c.trimester = undefined; }
                          }),
                        )}
                        {isPregnant && selectTd(
                          eff.trimester != null ? String(eff.trimester) : '',
                          trimesterOptions,
                          eff.displayTrimester,
                          (v) => patch((c) => { c.trimester = v ? Number(v) : undefined; }),
                        )}
                        {numberTd(eff.elevation, eff.displayElevation,
                          (v) => patch((c) => { c.elevation = v; }),
                          0, 20000, 'feet')}
                        {booleanTd(!!eff.cigaretteSmoker, eff.displayInhaledSmoke,
                          (v) => patch((c) => { c.cigaretteSmoker = v; }))}
                        {booleanTd(!!eff.alcoholDrinker, eff.displayAlcoholDrinker,
                          (v) => patch((c) => { c.alcoholDrinker = v; }))}
                        {previousCollectionDate && (
                          <td style={{ verticalAlign: 'middle' }}>
                            <Moment format="MM/DD/YYYY">{previousCollectionDate}</Moment>
                          </td>
                        )}
                        {heightTd()}
                        {weightTd()}
                      </tr>
                    </tbody>
                  </Table>
                  {this.props.onSave && (
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'center' }}>
                      {!editing ? (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={this.startEdit}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={this.save}
                            disabled={this.state.isSaving}
                          >
                            {this.state.isSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={this.cancelEdit}
                            disabled={this.state.isSaving}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {(() => {
                  const bmiAgeYears = eff.ageAsOf(collectionDate);
                  if (bmiNumber == null || isPregnant) return null;
                  if (bmiAgeYears != null && bmiAgeYears < 2) return null;
                  return (
                    <div style={{ marginTop: 12 }}>
                      <MemoBMIBarChart
                        bmi={bmiNumber}
                        ageYears={bmiAgeYears}
                        sex={bmiSex}
                      />
                    </div>
                  );
                })()}
                </>
              );
            })()}
            {this.props.preparedBy && (
              <div style={{ marginTop: 12 }}>
                <strong>Prepared by:</strong>{' '}
                {this.props.preparedBy}
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
